import { InputProps } from 'antd';
import React, { FC, lazy, Suspense } from 'react';
import type { ICurrencyFlagProps } from 'react-currency-flags';
import styled from 'styled-components';
import { Currency } from 'api/account';
import { CurrencyFlagSizes } from 'components/atoms/currency/types';
import { Input } from 'components/facade/input';
import { useAntdRlUISwitcher } from 'contexts/antd-rlui-switcher';

const LazyCurrencyFlag = lazy(() =>
  import('react-currency-flags').then((m) => ({
    default: m.default,
  })),
);

const currencyFlagFallbackStyle = (
  size: ICurrencyFlagProps['size'],
): React.CSSProperties => {
  switch (size) {
    case CurrencyFlagSizes.MEDIUM:
      return { width: 20, height: 15 };
    case CurrencyFlagSizes.LARGE:
      return { width: 24, height: 18 };
    case CurrencyFlagSizes.XLARGE:
      return { width: 32, height: 24 };
    case CurrencyFlagSizes.SMALL:
    default:
      return { width: 16, height: 12 };
  }
};

export enum CurrencyPrefixNotation {
  CurrencySymbol = 'CURRENCY_SYMBOL',
  CurrencyCode = 'CURRENCY_CODE',
  CurrencyCodeAndSymbol = 'CURRENCY_CODE_SYMBOL',
}

const K = 10000;
const M = 1000000;
const B = 1000000000;

/** Suffix letters used when `compactNotation` is true. Default is all three. */
export type CurrencyCompactSuffix = 'K' | 'M' | 'B';

export const DEFAULT_CURRENCY_COMPACT_SUFFIXES: readonly CurrencyCompactSuffix[] =
  ['K', 'M', 'B'];

const getAllowedCompactSuffixes = (
  compactSuffixes: ReadonlyArray<string> | undefined,
): readonly CurrencyCompactSuffix[] =>
  compactSuffixes === undefined
    ? DEFAULT_CURRENCY_COMPACT_SUFFIXES
    : compactSuffixes.length === 0
    ? []
    : DEFAULT_CURRENCY_COMPACT_SUFFIXES.filter((letter) =>
        compactSuffixes.some((s) => String(s).toUpperCase() === letter),
      );

/** Same digit grouping / fractional slicing as `currencyRenderer`, without K/M/B logic. */
const buildFormatterWithoutRounding = (
  precision: number,
  showTrailingZeros: boolean,
): ((value?: number) => string) => {
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: showTrailingZeros ? precision : 0,
    maximumFractionDigits: precision,
  });

  return (value = 0) => {
    const [wholePart, fractionalPart] = value.toString().split('.');

    const formattedWhole = formatter.format(Number(wholePart));

    if (precision <= 0 || fractionalPart == null) {
      return formattedWhole;
    }

    let slicedFraction = fractionalPart.slice(0, precision);

    if (slicedFraction.length === 0) {
      return formattedWhole;
    }

    // Strip any decimals the formatter added to the whole part (e.g. "10.00"
    // from showTrailingZeros) since we append the real fraction below.
    const formattedWholeOnly = formattedWhole.split('.')[0];

    if (showTrailingZeros) {
      slicedFraction = slicedFraction.padEnd(precision, '0');
    }

    return [formattedWholeOnly, slicedFraction].join('.');
  };
};

export const defaultCurrency: Currency = {
  currencyCode: 'USD',
  currencySymbol: '$',
  currencyName: 'US Dollar',
};

const removeTrailingZeroes = (amount: string) => amount.replace(/\.0+$/, '');

export const numberFormatter = (amount: number, precision = 2) =>
  Number(removeTrailingZeroes(amount.toFixed(precision)));

export const getCurrencyPrefix = (
  { currencyCode, currencySymbol } = defaultCurrency,
  notation = CurrencyPrefixNotation.CurrencyCode,
): string => {
  let prefixString = '';

  switch (notation) {
    case CurrencyPrefixNotation.CurrencySymbol:
      prefixString = currencySymbol;
      break;
    case CurrencyPrefixNotation.CurrencyCode:
      prefixString = `${currencyCode} `;
      break;
    case CurrencyPrefixNotation.CurrencyCodeAndSymbol:
      prefixString = `${currencyCode} ${currencySymbol}`;
      break;
    default:
      break;
  }

  return prefixString;
};

const BaseCurrencyInput = styled(Input)`
  input::placeholder {
    color: var(--black-100);
  }
`;

const addCurrencyPrefix = (
  val: string | undefined,
  currency: Currency,
  showCurrencyPrefix = true,
  notation: CurrencyPrefixNotation,
): string => {
  if (val == null || Number.isNaN(val)) {
    return '';
  }

  if (!showCurrencyPrefix) {
    return val;
  }

  const prefixString = getCurrencyPrefix(currency, notation);

  return `${prefixString}${val}`;
};

export const currencyRenderer = (
  amount = 0,
  currency = defaultCurrency,
  compactNotation = true,
  showCurrencyPrefix = true,
  precision = 2,
  notation = CurrencyPrefixNotation.CurrencyCode,
  showTrailingZeros = false,
  compactSuffixes?: ReadonlyArray<string>,
): string => {
  const formatterWithoutRounding = buildFormatterWithoutRounding(
    precision,
    showTrailingZeros,
  );

  const absoluteAmount = Math.abs(amount);
  const allowedSuffixes = getAllowedCompactSuffixes(compactSuffixes);

  if (compactNotation) {
    if (
      allowedSuffixes.includes('K') &&
      absoluteAmount >= K &&
      absoluteAmount < M
    ) {
      const formattedAmount = formatterWithoutRounding(amount / 1000);

      return `${addCurrencyPrefix(
        formattedAmount,
        currency,
        showCurrencyPrefix,
        notation,
      )}K`;
    }
    if (
      allowedSuffixes.includes('M') &&
      absoluteAmount >= M &&
      absoluteAmount < B
    ) {
      const formattedAmount = formatterWithoutRounding(amount / M);

      return `${addCurrencyPrefix(
        formattedAmount,
        currency,
        showCurrencyPrefix,
        notation,
      )}M`;
    }
    if (allowedSuffixes.includes('B') && absoluteAmount >= B) {
      const formattedAmount = formatterWithoutRounding(amount / B);

      return `${addCurrencyPrefix(
        formattedAmount,
        currency,
        showCurrencyPrefix,
        notation,
      )}B`;
    }
  }

  return `${addCurrencyPrefix(
    formatterWithoutRounding(amount),
    currency,
    showCurrencyPrefix,
    notation,
  )}`;
};

export const currencyFormatterForInput = (
  amount: string | number | undefined,
  currency: Currency,
  compactNotation = true,
  showCurrencyPrefix = true,
  precision = 2,
  notation = CurrencyPrefixNotation.CurrencyCode,
): string => {
  if (amount == null) {
    return '';
  }

  if (typeof amount === 'string') {
    const [wholePart, fractionalPart] = amount.split('.');

    return [
      `${currencyRenderer(
        Number(wholePart ?? 0),
        currency,
        compactNotation,
        showCurrencyPrefix,
        precision,
        notation,
      )}`,
      ...(fractionalPart != null ? [fractionalPart] : []),
    ].join('.');
  }

  return currencyRenderer(
    amount,
    currency,
    compactNotation,
    showCurrencyPrefix,
    precision,
    notation,
  );
};

export const CurrencyFlagWrapper = styled.div`
  display: flex;
  align-items: center;

  > span {
    border-radius: 1.5px;
    box-shadow: 0px 0.2px 0.5px 0px rgba(0, 0, 0, 0.15) inset;
  }
`;

const CurrencyFlagRendererComponent: FC<
  React.PropsWithChildren<ICurrencyFlagProps>
> = ({ currency, size, height, width }) => (
  <CurrencyFlagWrapper>
    <Suspense
      fallback={
        <span
          style={{
            ...currencyFlagFallbackStyle(size),
            display: 'inline-block',
            flexShrink: 0,
            verticalAlign: 'middle',
          }}
          aria-hidden
        />
      }
    >
      <LazyCurrencyFlag
        currency={currency}
        size={size}
        height={height}
        width={width}
      />
    </Suspense>
  </CurrencyFlagWrapper>
);

export const CurrencyFlagRenderer = React.memo(CurrencyFlagRendererComponent);

export const StyledCurrencyInput: FC<InputProps> = (props) => {
  const isNewDesignEnabled = useAntdRlUISwitcher();
  const { size, ...restProps } = props;

  const finalSize = isNewDesignEnabled && size == null ? 'middle' : size;

  return <BaseCurrencyInput {...restProps} size={finalSize} />;
};
